import React, { useContext, useMemo } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import Boost from 'highcharts/modules/boost';
import HC_brokenAxis from "highcharts/modules/broken-axis";

import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { useLicences } from "../../../context/LicenceContext";
import Helper from '../../../helpers/Helper';
import { getConvertedTimeToUTC } from "../../../helpers/utils";
import { chartOptionsGranularity } from '../../../helpers/chartOptionsIncreasedGranularity'; // Import the helper
import { CHART_COLOURS_STATEMENTS, CHART_DATA_TYPE } from '../../Constants';

// Re-use types from GroupDetailSlideout
interface TimeSeriesDataPoint {
    timeslice: string; // ISO timestamp string
    value: number;
}

interface InstanceSeriesData {
    instanceId: number;
    instanceName: string;
    data: TimeSeriesDataPoint[];
}

// Initialize Highcharts modules
HC_brokenAxis(Highcharts);
Boost(Highcharts);

interface MultiInstanceTimeSeriesChartProps {
    seriesData: InstanceSeriesData[];
    yAxisLabel: string;
    valueSuffix?: string;
    chartType?: 'spline' | 'line' | 'area' | 'column';
}

 const MultiInstanceTimeSeriesChart: React.FC<MultiInstanceTimeSeriesChartProps> = ({
    seriesData,
    yAxisLabel,
    valueSuffix = '',
    chartType = 'spline',
}) => {
    const timeRangeContext = useContext(TimeRangeContext); // Get full context
    const { timeRange } = timeRangeContext;
    const { licences } = useLicences();

    const highchartsSeries = useMemo(() => seriesData.map((instanceSeries, index) => ({
        name: instanceSeries.instanceName,
        data: instanceSeries.data.map(point => [getConvertedTimeToUTC(point), point.value]),
        type: chartType,
        color: CHART_COLOURS_STATEMENTS[index % CHART_COLOURS_STATEMENTS.length],
        tooltip: { valueSuffix: ` ${valueSuffix}` },
        marker: { enabled: chartType !== 'area', radius: 2, symbol: 'circle' },
        lineWidth: chartType === 'area' ? 0 : 2,
    })), [seriesData, chartType, valueSuffix]);

    const chartOptions = useMemo(() => {

        {
            // Prepare options for chartOptionsGranularity
            const granularityOptions = {
                timeRangeContext: timeRangeContext,
                legend: { enabled: true },
                yAxis: [{
                    title: { text: yAxisLabel },
                    min: 0,
                    labels: {
                        formatter: function(this: Highcharts.AxisLabelsFormatterContextObject): string { // Ensure return type is string
                            const value = this.value;
                            if (valueSuffix === 'ms') {
                                return Helper.getTimeInEnglish(Number(value));
                            }
                            return String(Highcharts.numberFormat(Number(value), 0, '.', ','));
                        }
                    }
                }],
                tooltip: {
                    shared: true,
                    useHTML: true,
                    outside: true,
                    formatter: function () {
                        return Helper.getChartTooltipsNew(this, valueSuffix === 'ms' ? CHART_DATA_TYPE.TIME : CHART_DATA_TYPE.GENERIC);
                    }
                },
                ...(chartType === 'column' && { minHeight: 2 })
            };

            let maxVal = 0;
            highchartsSeries.forEach(series => {
                series.data.forEach(point => {
                    // Ensure point[1] is treated as a number
                    const value = Number(point[1]);
                    if (!isNaN(value) && value > maxVal) {
                         maxVal = value;
                    }
                });
            });

            // Call the granularity helper
            // Explicitly cast return type to Highcharts.Options
            return chartOptionsGranularity(highchartsSeries, maxVal, granularityOptions, 200) as Highcharts.Options;
        }

    }, [highchartsSeries, yAxisLabel, valueSuffix, chartType, timeRange.from.valueOf(), timeRange.to.valueOf(), timeRange.interval, licences]);

    return (
        <div>
            <HighchartsReact
                highcharts={Highcharts}
                options={chartOptions}
                constructorType={"chart"}
            />
        </div>
    );
};

export default MultiInstanceTimeSeriesChart;
